import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export type TimezoneOption = {
  value: string;
  label: string;
  offset: number;
};

const getCurrentOffsetMinutes = (tz: string): number => {
  try {
    return dayjs().tz(tz).utcOffset();
  } catch {
    return 0;
  }
};

type CuratedTimezone = {
  value: string;
  label: string;
};

const CURATED_TIMEZONES: CuratedTimezone[] = [
  { value: "UTC", label: "UTC" },

  // Americas
  { value: "America/New_York", label: "New York" },
  { value: "America/Chicago", label: "Dallas" },
  { value: "America/Denver", label: "Denver" },
  { value: "America/Los_Angeles", label: "Los Angeles" },
  { value: "America/Mexico_City", label: "Mexico City" },

  // Europe
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },

  // Africa / Middle East
  { value: "Africa/Johannesburg", label: "Johannesburg" },
  { value: "Asia/Dubai", label: "Dubai" },

  // Asia
  { value: "Asia/Kolkata", label: "India" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo" },

  // Australia
  { value: "Australia/Perth", label: "Perth" },
  { value: "Australia/Darwin", label: "Darwin" },
  { value: "Australia/Adelaide", label: "Adelaide" },
  { value: "Australia/Brisbane", label: "Brisbane" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Australia/Melbourne", label: "Melbourne" },
  { value: "Australia/Hobart", label: "Hobart" },

  // NZ
  { value: "Pacific/Auckland", label: "Auckland" },
];

interface TimezoneContextType {
  selectedTimezone: string;
  setSelectedTimezone: (timezone: string) => void;
  userTimezone: string;
  projectTimezone: string | null;
  setProjectTimezone: (timezone: string | null) => void;
  timezoneOptions: TimezoneOption[];
}

const resolveUserTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "UTC";
  } catch {
    return "UTC";
  }
};

/** "Australia/Brisbane" -> "Brisbane"; a zone without a region ("UTC",
 *  "EST") keeps its name. Indexing [1] blindly threw on those and the app
 *  failed to mount (fisci-front-end #404). */
const tzLabel = (tz: string): string => (tz.split("/")[1] ?? tz).replace(/_/g, " ");

const listSupportedTimezones = (): string[] => {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  }).supportedValuesOf;
  if (typeof supportedValuesOf === "function") {
    try {
      return supportedValuesOf("timeZone") as string[];
    } catch {
      return [];
    }
  }
  return [];
};

const buildTimezoneOptions = (userTimezone: string, projectTimezone?: string | null) => {
  const supportedSet = new Set(listSupportedTimezones());
  const seen = new Set<string>();
  const options: TimezoneOption[] = [];

  const push = (value: string, label: string, offset: number) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label, offset });
  };

  push(
      userTimezone,
      tzLabel(userTimezone),
      getCurrentOffsetMinutes(userTimezone)
  );
  if (projectTimezone) {
    push(
        projectTimezone,
        tzLabel(projectTimezone),
        getCurrentOffsetMinutes(projectTimezone)
    );
  }

  // 3. Curated World List
  const remaining = CURATED_TIMEZONES
      .filter((tz) => !seen.has(tz.value))
      .filter((tz) => tz.value === "UTC" || supportedSet.size === 0 || supportedSet.has(tz.value))
      .map((tz) => ({ ...tz, offset: getCurrentOffsetMinutes(tz.value) }))
      .sort((a, b) => (a.offset || 0) - (b.offset || 0));

  remaining.forEach(({ value, label, offset }) => {
    push(value, label, offset);
  });

  return options;
};

const TimezoneContext = createContext<TimezoneContextType>({
  selectedTimezone: "UTC",
  setSelectedTimezone: () => {},
  userTimezone: "UTC",
  projectTimezone: null,
  setProjectTimezone: () => {},
  timezoneOptions: [{ value: "UTC", label: "UTC", offset: 0}],
});

export const useTimezone = () => useContext(TimezoneContext);

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userTimezone = useMemo(() => resolveUserTimezone(), []);

  // Internal state for the project's specific timezone
  const [projectTimezone, setProjectTimezone] = useState<string | null>(null);

  // The current active timezone for the UI
  const [selectedTimezone, setSelectedTimezoneState] = useState<string>(userTimezone);

  const hasUserSelectedRef = useRef(false);

  // When the project changes, automatically update selectedTimezone if user hasn't manually picked one
  useEffect(() => {
    if (projectTimezone && !hasUserSelectedRef.current) {
      setSelectedTimezoneState(projectTimezone);
    }
  }, [projectTimezone]);

  const setSelectedTimezone = (tz: string) => {
    hasUserSelectedRef.current = true;
    setSelectedTimezoneState(tz);
  };

  const timezoneOptions = useMemo(
      () => buildTimezoneOptions(userTimezone, projectTimezone),
      [projectTimezone, userTimezone]
  );

  return (
      <TimezoneContext.Provider
          value={{
            selectedTimezone,
            setSelectedTimezone,
            userTimezone,
            projectTimezone,
            setProjectTimezone, // Exposed to allow updates from outside
            timezoneOptions,
          }}
      >
        {children}
      </TimezoneContext.Provider>
  );
};
